/* eslint-disable react/display-name */
import React, { useState, useRef, type ChangeEvent } from 'react';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import { type AlertColor } from '@mui/material';

import { PhotoPanelBox } from './FileBox';
import { type PhotoPanelData, FILE_MANAGE_ORDER, type FileData } from './types';
import { COMMON_FILE_TEXT, PHOTO_SIZE_LIMITS } from './constants';
import { Alert, type AlertDetails, Loading } from '../';
import { postRequest, type PostRequestData } from '../../utils';

export const PhotoPanel: React.FunctionComponent<PhotoPanelData> = (
  props: PhotoPanelData,
) => {
  const {
    title,
    caption,
    files,
    disabled,
    handleFilesChange,
    onPhotoBaseChange,
  } = props;

  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const handleDisplayAlert = (
    alertType: AlertColor,
    alertContent: string,
  ): void => {
    setOpenAlert(true);
    setAlertDetails({
      alertType,
      alertContent,
    });
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.target.files === null || e.target.files.length < 1) return;

    const file = e.target.files?.[0];
    if (file.type !== 'image/jpeg') {
      handleDisplayAlert('warning', 'Only JPG format is accepted.');
      return;
    }

    if (
      file.size < PHOTO_SIZE_LIMITS.MIN_SIZE ||
      file.size > PHOTO_SIZE_LIMITS.MAX_SIZE
    ) {
      setLoading(false);

      handleDisplayAlert('warning', COMMON_FILE_TEXT.PHOTO_LIMIT);

      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      onPhotoBaseChange(base64String);
    };
    reader.readAsDataURL(file);

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const postRequestData: PostRequestData = {
        url: '/files/upload',
        data: formData,
        responseType: 'json',
      };

      const res = await postRequest(postRequestData);
      const id = res.data.urls[0];
      const newFile = { id, name: file.name, link: '' };
      handleFilesChange(FILE_MANAGE_ORDER.UPLOAD, newFile.id);
      handleDisplayAlert('success', 'Uploaded the Photo Successfully.');
    } catch (error: any) {
      handleDisplayAlert(
        'error',
        'Uploaded the Photo Failed.' + error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (fileId: string, index: number): void => {
    // Mod 2024/9/16, not delete from server any more.
    // const res = await axios.delete(`/files/${fileId}`);

    handleFilesChange(FILE_MANAGE_ORDER.REMOVE, fileId);

    setAlertDetails({
      alertContent: 'Removed the File Successfully.',
      alertType: 'success',
    });
    setOpenAlert(true);
  };

  const hasPhoto = Boolean(files?.id);

  const placeHolderFile: FileData = {
    id: '',
    name: '',
    link: '',
  };
  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <div>
          {Boolean(title) && <InfoBox>{title}</InfoBox>}
          <InfoBox>{caption}</InfoBox>
          <InfoBox $bottom={true}>
            {hasPhoto ? (
              <div className="uploaded-file">
                <PhotoPanelBox
                  index={0}
                  fileExtension="jpg"
                  file={files ?? placeHolderFile}
                  fileInputRef={fileInputRef}
                  reUpload={uploadFile}
                  removeFile={removeFile}
                  disabled={disabled}
                  handleDisplayAlert={handleDisplayAlert}
                />
              </div>
            ) : (
              <div
                className="upload-file"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                <input
                  key={1}
                  ref={fileInputRef}
                  type="file"
                  onChange={uploadFile}
                  accept=".jpg"
                />
                <AddIcon />
              </div>
            )}
          </InfoBox>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div<{
  $bottom?: boolean;
}>`
  width: 12rem;
  height: ${props => (props?.$bottom ? '12rem' : 'auto')};
  border: 1px solid rgba(124, 124, 124, 0.25);
  border-bottom: ${props =>
    props?.$bottom ? '1px solid  rgba(124, 124, 124, 0.25)' : 'none'};
  padding: 0.75rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center; /* Ensure content is centered vertically */

  .upload-file,
  .uploaded-file {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .upload-file {
    flex-direction: column;
    border-radius: 0.5rem;
    margin: 0.5rem 0.2rem;
    cursor: pointer;

    input {
      display: none;
    }
  }
`;
